import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  Divider,
  Tooltip,
  InputAdornment,
  Stack,
  Alert,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Menu,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  useTheme,
  useMediaQuery,
  Drawer,
} from '@mui/material';
import { ChatContainer, ChatMessage } from '../components/Chat';
import InformationPanelPermanent from '../components/InformationPanel/InformationPanelPermanent';
import {
  Send,
  Chat as MessageSquare,
  ThumbUp as ThumbsUp,
  ThumbDown as ThumbsDown,
  Edit as Edit3,
  Description as FileIcon,
  Folder as FolderIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  History as HistoryIcon,
  AutoAwesome as SparkleIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import { chatAPI, feedbackAPI, getProjects, getProjectFiles } from '../services/api';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Document as DocxDocument, Packer, Paragraph, HeadingLevel } from 'docx';
import { saveAs } from 'file-saver';

interface Message {
  id: number;
  stream_id?: number;
  type: 'user' | 'assistant';
  message: string;
  sources?: any[];
  document_references?: number[];
  citations?: any[];
  mindmap_markdown?: string | null;
  mindmap_metadata?: any;
  pipeline_metadata?: any;
  timestamp: string;
  is_error?: boolean;
  processing_time_ms?: number;
  feedback?: {
    feedback_type: string;
    note?: string;
    created_at: string;
  };
}

interface Feedback {
  feedback_type: string | null;
  note?: string | null;
  created_at: string;
}

interface Project {
  id: number;
  name: string;
  description?: string;
}

interface Document {
  id: number;
  name?: string;
  filename: string;
  display_name?: string;
  original_filename?: string;
  file_type?: string;
  status?: string;
}

interface MessageFeedbackProps {
  messageId: number;
  initialFeedback?: Feedback | null;
  message: string;
}

const MessageFeedback: React.FC<MessageFeedbackProps> = ({ messageId, initialFeedback, message }) => {
  const messageRef = useRef<HTMLDivElement>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(initialFeedback || null);
  const [isEditing, setIsEditing] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copyTooltip, setCopyTooltip] = useState('Copy response');
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false); // New state for download loader

  const handleCopy = () => {
    const plainText = message.replace(/<[^>]*>/g, '');
    navigator.clipboard.writeText(plainText);
    setCopyTooltip('Copied!');
    setTimeout(() => setCopyTooltip('Copy response'), 2000);
  };

  const handleDownload = async (format: 'md' | 'txt' | 'pdf' | 'docx') => {
    setDownloadMenuAnchor(null);
    setIsDownloading(true); // Start loading

    // Add a 5-second delay before proceeding with the download
    await new Promise(resolve => setTimeout(resolve, 500));

    let content = message;
    let mimeType = '';
    let fileExtension = '';

    // Helper to convert markdown to plain text for DOCX
    const markdownToPlainText = (md: string) => {
      return md
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links
        .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1') // images
        .replace(/(\*\*|__)(.*?)\1/g, '$2') // bold
        .replace(/(\*|_)(.*?)\1/g, '$2') // italic
        .replace(/`{1,3}(.*?)`{1,3}/g, '$1') // code
        .replace(/#+\s/g, '') // headers
        .replace(/-\s/g, '') // list items
        .replace(/>\s/g, ''); // blockquotes
    };

    switch (format) {
      case 'md':
        mimeType = 'text/markdown';
        fileExtension = 'md';
        break;
      case 'txt':
        // Simple markdown to text conversion
        content = content
          .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links
          .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1') // images
          .replace(/(\*\*|__)(.*?)\1/g, '$2') // bold
          .replace(/(\*|_)(.*?)\1/g, '$2') // italic
          .replace(/`{1,3}(.*?)`{1,3}/g, '$1') // code
          .replace(/#+\s/g, '') // headers
          .replace(/-\s/g, '') // list items
          .replace(/>\s/g, ''); // blockquotes
        mimeType = 'text/plain';
        fileExtension = 'txt';
        break;
      case 'pdf':
        // Find the parent message element that contains the actual content
        const messageContainer = messageRef.current?.closest('.MuiBox-root');
        if (messageContainer) {
          // Create a temporary clone to avoid modifying the visible DOM
          const tempContainer = messageContainer.cloneNode(true) as HTMLElement;

          // Hide feedback buttons in the clone
          const feedbackElements = tempContainer.querySelectorAll('.message-feedback');
          feedbackElements.forEach(el => {
            (el as HTMLElement).style.display = 'none';
          });

          // Ensure all backgrounds are white, text is black, and borders are removed
          const allElements = tempContainer.querySelectorAll('*');
          allElements.forEach(el => {
            const element = el as HTMLElement;
            element.style.backgroundColor = '#ffffff';
            element.style.color = '#000000';
            element.style.border = 'none';
            element.style.borderRadius = '0';
            element.style.boxShadow = 'none';
            element.style.backgroundImage = 'none';
          });
          tempContainer.style.backgroundColor = '#ffffff';
          tempContainer.style.color = '#000000';
          tempContainer.style.border = 'none';
          tempContainer.style.borderRadius = '0';
          tempContainer.style.boxShadow = 'none';
          tempContainer.style.fontSize = '0.75rem'; // Reduce base font size

          // Temporarily append to body but hide it
          tempContainer.style.position = 'absolute';
          tempContainer.style.left = '-9999px';
          tempContainer.style.width = '210mm'; // A4 width
          tempContainer.style.padding = '20mm'; // Add padding around the content
          tempContainer.style.boxSizing = 'border-box'; // Include padding in width
          document.body.appendChild(tempContainer);

          html2canvas(tempContainer, {
            scale: 2, // Increase scale for better resolution
            useCORS: true,
            backgroundColor: '#ffffff',
            logging: false,
            windowWidth: tempContainer.scrollWidth, // Capture full scrollable width
            windowHeight: tempContainer.scrollHeight // Capture full scrollable height
          }).then(async canvas => {
            const imgData = canvas.toDataURL('image/jpeg', 0.8); // Use JPEG with quality 80%
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight(); // A4 height in mm
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;

            const ratio = pdfWidth / imgWidth;
            const imgHeightScaled = imgHeight * ratio;

            let heightLeft = imgHeightScaled;
            let position = 0;

            // Add first page
            pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, imgHeightScaled);
            heightLeft -= pdfHeight;

            // Add subsequent pages
            while (heightLeft > 0) {
              position = heightLeft - imgHeightScaled; // Calculate position for the next slice
              pdf.addPage();
              pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, imgHeightScaled);
              heightLeft -= pdfHeight;
            }
            pdf.save(`response-${messageId}.pdf`);

            // Clean up
            document.body.removeChild(tempContainer);
          });
        } else {
          // Fallback to text-based PDF if element not found
          const pdf = new jsPDF();
          pdf.text(content.replace(/<[^>]*>/g, ''), 10, 10);
          pdf.save(`response-${messageId}.pdf`);
        }
        break;
    case 'docx':
      const plainTextContent = markdownToPlainText(content);
      const paragraphs = plainTextContent.split('\n').map(line => {
        if (line.startsWith('### ')) {
          return new Paragraph({ text: line.substring(4), heading: HeadingLevel.HEADING_3 });
        } else if (line.startsWith('## ')) {
          return new Paragraph({ text: line.substring(3), heading: HeadingLevel.HEADING_2 });
        } else if (line.startsWith('# ')) {
          return new Paragraph({ text: line.substring(2), heading: HeadingLevel.HEADING_1 });
        } else if (line.startsWith('- ')) {
          return new Paragraph({ text: line.substring(2), bullet: { level: 0 } });
        } else if (line.match(/^\d+\. /)) {
          return new Paragraph({ text: line.substring(line.indexOf('.') + 2), numbering: { reference: "my-numbering", level: 0 } });
        }
        return new Paragraph({ text: line });
      });

      const doc = new DocxDocument({
        sections: [{
          properties: {},
          children: paragraphs,
        }],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, `response-${messageId}.docx`);
      setIsDownloading(false); // End loading
      return;
    }
    
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `response-${messageId}.${fileExtension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setIsDownloading(false); // End loading
  };

  useEffect(() => {
    if (initialFeedback) {
      setFeedback(initialFeedback);
      setNoteText(initialFeedback.note || '');
    }
  }, [initialFeedback]);

  const handleFeedbackClick = async (feedbackType: string | null) => {
    setIsLoading(true);
    setError(null);

    try {
      if (feedback?.feedback_type === feedbackType) {
        await feedbackAPI.deleteFeedback(messageId);
        setFeedback(null);
        setNoteText('');
      } else {
        await feedbackAPI.submitFeedback(messageId, feedbackType, noteText || undefined);
        setFeedback({
          feedback_type: feedbackType,
          note: noteText || null,
          created_at: new Date().toISOString(),
        });
      }
    } catch (err) {
      setError('Failed to submit feedback');
      console.error('Feedback error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNoteEdit = () => {
    setIsEditing(true);
    setNoteText(feedback?.note || '');
  };

  const handleNoteSave = async () => {
    if (!feedback && !noteText.trim()) {
      setNoteDialogOpen(false);
      return;
    }

    const feedbackType = feedback?.feedback_type || null;

    setIsLoading(true);
    setError(null);

    try {
      await feedbackAPI.submitFeedback(messageId, feedbackType, noteText || undefined);
      setFeedback(prev => ({
        ...(prev || { created_at: new Date().toISOString() }),
        feedback_type: feedbackType,
        note: noteText || null,
      }));
      setIsEditing(false);
      setNoteDialogOpen(false);
    } catch (err) {
      setError('Failed to save note');
      console.error('Note save error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNoteCancel = () => {
    setIsEditing(false);
    setNoteText(feedback?.note || '');
    setNoteDialogOpen(false);
  };

  return (
    <div ref={messageRef} className="message-feedback">
      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Tooltip title="Good response">
          <IconButton
            size="small"
            onClick={() => handleFeedbackClick('thumbs_up')}
            disabled={isLoading}
            color={feedback?.feedback_type === 'thumbs_up' ? 'success' : 'default'}
            sx={{
              border: feedback?.feedback_type === 'thumbs_up' ? '1px solid' : 'none',
              borderColor: 'success.main',
            }}
          >
            <ThumbsUp fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Poor response">
          <IconButton
            size="small"
            onClick={() => handleFeedbackClick('thumbs_down')}
            disabled={isLoading}
            color={feedback?.feedback_type === 'thumbs_down' ? 'error' : 'default'}
            sx={{
              border: feedback?.feedback_type === 'thumbs_down' ? '1px solid' : 'none',
              borderColor: 'error.main',
            }}
          >
            <ThumbsDown fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title={copyTooltip}>
          <IconButton size="small" onClick={handleCopy}>
            <CopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Download response">
          <IconButton
            size="small"
            onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
            disabled={isDownloading} // Disable button while downloading
          >
            {isDownloading ? <CircularProgress size={20} /> : <DownloadIcon fontSize="small" />}
          </IconButton>
        </Tooltip>

        <Tooltip title={feedback?.note ? "View/Edit Note" : "Add Note"}>
          <IconButton size="small" onClick={() => setNoteDialogOpen(true)}>
            <Edit3 fontSize="small" />
          </IconButton>
        </Tooltip>

        {error && (
          <Typography variant="caption" color="error" sx={{ ml: 1 }}>
            {error}
          </Typography>
        )}
      </Box>
      <Dialog open={noteDialogOpen} onClose={handleNoteCancel} fullWidth maxWidth="sm">
        <DialogTitle>Feedback Note</DialogTitle>
        <DialogContent>
          {isEditing ? (
            <TextField
              autoFocus
              margin="dense"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add a detailed note..."
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              sx={{ mt: 2 }}
            />
          ) : (
            <DialogContentText sx={{ mt: 2 }}>
              {feedback?.note || 'No note has been added yet.'}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px' }}>
          {isEditing ? (
            <>
              <Button onClick={handleNoteCancel}>Cancel</Button>
              <Button onClick={handleNoteSave} variant="contained" disabled={isLoading}>
                Save Note
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleNoteCancel}>Close</Button>
              <Button onClick={handleNoteEdit} variant="contained">
                {feedback?.note ? 'Edit Note' : 'Add Note'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={() => setDownloadMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleDownload('md')}>Markdown (.md)</MenuItem>
        <MenuItem onClick={() => handleDownload('txt')}>Text (.txt)</MenuItem>
        <MenuItem onClick={() => handleDownload('pdf')}>PDF (.pdf)</MenuItem>
        <MenuItem onClick={() => handleDownload('docx')}>Word (.docx)</MenuItem>
      </Menu>
    </div>
  );
};


const Chat: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg')); // Define large screen for permanent panel
  const [isInfoPanelOpen, setIsInfoPanelOpen] = useState(isLargeScreen); // Default open for large screens, collapsed (20%) for others

  const toggleInfoPanel = () => {
    setIsInfoPanelOpen(prev => !prev);
  };

  const [messages, setMessages] = useState<Message[]>([
    {
      id: Date.now(),
      type: 'assistant',
      message: "Hello! I'm your AI assistant ready to help you analyze your documents. Please select a project and documents to get started, then ask me anything about your content.",
      timestamp: new Date().toISOString()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [selectedDocuments, setSelectedDocuments] = useState<number[]>([]);
  const [projectDocuments, setProjectDocuments] = useState<Document[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  const loadProjectDocuments = useCallback(async () => {
    if (!selectedProject) {
      setProjectDocuments([]);
      setSelectedDocuments([]);
      return;
    }

    setLoadingDocuments(true);
    try {
      // Using the existing documents API from AnCore
      const data = await getProjectFiles(selectedProject);
      const allDocs = data.files || [];

      // Filter to only show AI-ready documents (completed embeddings)
      const aiReadyDocs = allDocs.filter((doc: any) => {
        // Check if document has completed embeddings
        return doc.embedding_status === 'completed' ||
          (doc.tags && doc.tags.includes('ai-ready'));
      });

      setProjectDocuments(aiReadyDocs);

      // Auto-select all AI-ready documents
      const docIds = aiReadyDocs.map((doc: any) => doc.id);
      setSelectedDocuments(docIds);
    } catch (error: any) {
      console.error('Error loading project documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  }, [selectedProject, setProjectDocuments, setSelectedDocuments, setLoadingDocuments]);

  const loadChatHistory = useCallback(async () => {
    try {
      const projectId = selectedProject ? selectedProject : undefined;
      const response = await chatAPI.getChatHistory(projectId);
      if (response.messages && response.messages.length > 0) {
        setMessages(response.messages);
      }
    } catch (err: any) {
      console.error('Failed to load chat history:', err);
      // Keep welcome message if no history
    }
  }, [selectedProject, setMessages]);

  const [documentMenuAnchor, setDocumentMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedCitationIndex, setSelectedCitationIndex] = useState<number | undefined>(undefined);
  const [selectedMessageId, setSelectedMessageId] = useState<number | null>(null);
  const [documentSearchTerm, setDocumentSearchTerm] = useState('');
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingMessageId, setLoadingMessageId] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [clearChatDialogOpen, setClearChatDialogOpen] = useState(false);
  const [isDeletingChat, setIsDeletingChat] = useState(false); // New state for delete loading
  const [pdfCache, setPdfCache] = useState<Map<string, string>>(new Map());
  const [deepResearchEnabled, setDeepResearchEnabled] = useState(false);
  const [deepResearchDialogOpen, setDeepResearchDialogOpen] = useState(false);
  const [isQuickAnalysisCollapsed, setIsQuickAnalysisCollapsed] = useState(false);

  // Preload PDFs when citations are received
  useEffect(() => {
    const selectedMsg = selectedMessageId
      ? messages.find(m => m.id === selectedMessageId)
      : [...messages].reverse().find(m => m.type === 'assistant');

    if (selectedMsg?.pipeline_metadata?.citations?.citations) {
      const citations = selectedMsg.pipeline_metadata.citations.citations;

      // Get unique document IDs
      const uniqueDocIds = new Set<string>();
      citations.forEach((citation: any) => {
        if (citation.document_id && !pdfCache.has(citation.document_id)) {
          uniqueDocIds.add(citation.document_id);
        }
      });

      // Preload PDFs for all unique documents
      uniqueDocIds.forEach(docId => {
        const url = `${process.env.REACT_APP_API_BASE_URL}/api/v1/documents/${docId}/download`;
        setPdfCache(prev => new Map(prev).set(docId, url));
      });
    }
  }, [messages, selectedMessageId, pdfCache]);

  // Load cached state from localStorage on mount
  useEffect(() => {
    if (projectId) {
      setSelectedProject(projectId);
    } else {
      const cachedProject = localStorage.getItem('chat_selected_project');
      if (cachedProject) {
        setSelectedProject(cachedProject);
      }
    }
  }, [projectId]);

  useEffect(() => {
    const cachedDocuments = localStorage.getItem('chat_selected_documents');
    const cachedMessages = localStorage.getItem('chat_messages');

    if (cachedDocuments) {
      try {
        const docs = JSON.parse(cachedDocuments);
        setSelectedDocuments(docs);
      } catch (e) {
        console.error('Failed to parse cached documents:', e);
      }
    }

    if (cachedMessages) {
      try {
        const msgs = JSON.parse(cachedMessages);
        if (msgs.length > 0) {
          setMessages(msgs);
        }
      } catch (e) {
        console.error('Failed to parse cached messages:', e);
      }
    }
  }, []);

  // Cache state to localStorage whenever it changes
  useEffect(() => {
    if (selectedProject) {
      localStorage.setItem('chat_selected_project', selectedProject);
    } else {
      localStorage.removeItem('chat_selected_project');
    }
  }, [selectedProject]);

  useEffect(() => {
    if (selectedDocuments.length > 0) {
      localStorage.setItem('chat_selected_documents', JSON.stringify(selectedDocuments));
    } else {
      localStorage.removeItem('chat_selected_documents');
    }
  }, [selectedDocuments]);

  useEffect(() => {
    // Only cache messages if there's more than just the welcome message
    if (messages.length > 1) {
      localStorage.setItem('chat_messages', JSON.stringify(messages));
    }
  }, [messages]);

  // Suggested prompts for better UX
  const suggestedPrompts = [
    "Summarize the key points from the selected documents",
    "What are the main risks mentioned in these documents?",
    "Extract all financial metrics and numbers",
    "What are the key recommendations or conclusions?",
    "Compare different sections or documents",
  ];

  // Loading messages that cycle while waiting for stream
  const loadingMessages = [
    "Thinking...",
    "Analyzing documents...",
    "Processing your query...",
    "Searching through content...",
    "Preparing response...",
    "Almost ready..."
  ];

  // Smart scrolling functions
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setShowScrollButton(false);
    setShouldAutoScroll(true);
  };

  const checkScrollPosition = useCallback(() => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

      // Show scroll button if user is not near bottom and there are messages
      setShowScrollButton(!isNearBottom && messages.length > 1);

      // Update auto-scroll preference based on user position
      setShouldAutoScroll(isNearBottom);
    }
  }, [messages.length, setShowScrollButton, setShouldAutoScroll]);

  const handleScroll = useCallback(() => {
    setIsUserScrolling(true);
    checkScrollPosition();

    // Clear the scrolling flag after a delay
    setTimeout(() => {
      setIsUserScrolling(false);
    }, 150);
  }, [checkScrollPosition, setIsUserScrolling]);

  // Smart auto-scroll: only scroll if user is at bottom or during streaming
  useEffect(() => {
    if (shouldAutoScroll && !isUserScrolling) {
      scrollToBottom();
    }
  }, [messages, shouldAutoScroll, isUserScrolling]);

  // Force auto-scroll when loading starts (new message being generated)
  useEffect(() => {
    if (isLoading) {
      setShouldAutoScroll(true);
      scrollToBottom();
    }
  }, [isLoading]);

  // Set up scroll listener
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Initial scroll position check
  useEffect(() => {
    checkScrollPosition();
  }, [messages, checkScrollPosition]);

  // Get selected message for info panel, or fallback to latest assistant message
  const selectedMessage = selectedMessageId
    ? messages.find(m => m.id === selectedMessageId)
    : [...messages].reverse().find(m => m.type === 'assistant');

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      loadChatHistory();
      loadProjectDocuments();
    }
  }, [selectedProject, loadChatHistory, loadProjectDocuments]);

  // Set up global citation click handler to expand in info panel
  useEffect(() => {
    (window as any).showSourceModal = (sourceIndex: number, messageId?: number) => {
      console.log('🔗 Citation clicked:', { sourceIndex, messageId, currentSelectedMessageId: selectedMessageId });

      // Open the information panel
      setIsInfoPanelOpen(true);

      // Find the message
      const targetMessage = messageId ? messages.find(m => m.id === messageId) : selectedMessage;

      if (!targetMessage) {
        console.warn('⚠️ Target message not found');
        return;
      }

      // Get the source from the sources array
      const source = targetMessage.sources?.[sourceIndex];
      if (!source) {
        console.warn('⚠️ Source not found at index:', sourceIndex);
        return;
      }

      console.log('📄 Source found:', { page: source.metadata?.page, docId: source.metadata?.document_id });

      // Find matching citation in the citations array by document_id and page
      const citations = targetMessage.pipeline_metadata?.citations?.citations || [];

      // Log all citations for debugging
      console.log('📋 All citations:', citations.map((c: any, idx: number) => ({
        index: idx,
        docId: c.document_id,
        page: c.page,
        score: c.score
      })));

      const citationIndex = citations.findIndex((c: any) =>
        c.document_id === source.metadata?.document_id &&
        c.page === source.metadata?.page
      );

      console.log('🔍 Looking for citation:', {
        docId: source.metadata?.document_id,
        page: source.metadata?.page,
        foundIndex: citationIndex,
        totalCitations: citations.length
      });

      if (citationIndex === -1) {
        console.warn('⚠️ Citation not found in citations array');
        return;
      }

      // If clicking a citation from a different message, switch to that message
      if (messageId && messageId !== selectedMessageId) {
        console.log('📝 Switching to message:', messageId);
        setSelectedMessageId(messageId);
        // Reset citation index when switching messages, then set it after a brief delay
        setSelectedCitationIndex(undefined);
        setTimeout(() => {
          console.log('✅ Setting citation index:', citationIndex);
          setSelectedCitationIndex(citationIndex);
        }, 100);
      } else {
        // Same message, just expand the citation
        console.log('✅ Setting citation index:', citationIndex);
        setSelectedCitationIndex(citationIndex);
      }
    };

    return () => {
      delete (window as any).showSourceModal;
    };
  }, [messages, selectedMessageId, selectedMessage, setIsInfoPanelOpen]);

  const loadProjects = async () => {
    setLoadingProjects(true);
    try {
      // Using the existing projects API from AnCore
      const data = await getProjects({
        skip: 0,
        size: 1000,
      });
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };


  const handleProjectChange = (projectId: string) => {
    setSelectedProject(projectId);
    setMessages([
      {
        id: Date.now(),
        type: 'assistant',
        message: "Project selected! Loading documents...",
        timestamp: new Date().toISOString()
      }
    ]);
  };

  const handleDocumentToggle = (documentId: number) => {
    setSelectedDocuments(prev =>
      prev.includes(documentId)
        ? prev.filter(id => id !== documentId)
        : [...prev, documentId]
    );
  };

  const handleToggleAllDocuments = () => {
    const filteredDocs = projectDocuments.filter(doc =>
      (doc.name || doc.display_name || doc.original_filename || doc.filename || '')
        .toLowerCase()
        .includes(documentSearchTerm.toLowerCase())
    );
    const allSelected = selectedDocuments.length === filteredDocs.length;
    setSelectedDocuments(allSelected ? [] : filteredDocs.map(doc => doc.id));
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!inputMessage.trim() || isLoading) {
      return;
    }

    if (selectedDocuments.length === 0) {
      setError('Please select at least one document to query');
      return;
    }

    const userMessage: Message = {
      id: Date.now(),
      type: 'user',
      message: inputMessage.trim(),
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setError(null);

    // Clear info panel when sending new message
    setSelectedMessageId(null);
    setSelectedCitationIndex(undefined);

    // Create placeholder assistant message for streaming with loading state
    const assistantMessageId = Date.now() + 1;
    const assistantMessage: Message = {
      id: assistantMessageId,
      stream_id: assistantMessageId,
      type: 'assistant',
      message: loadingMessages[0],
      sources: [],
      document_references: [],
      timestamp: new Date().toISOString(),
      is_error: false,
    };

    setMessages(prev => [...prev, assistantMessage]);
    setLoadingMessageId(assistantMessageId);

    // Start cycling through loading messages
    let messageIndex = 0;
    const loadingInterval = setInterval(() => {
      messageIndex = (messageIndex + 1) % loadingMessages.length;
      const newLoadingText = loadingMessages[messageIndex];

      // Update the assistant message with new loading text
      setMessages(prev => prev.map(msg =>
        (msg.stream_id || msg.id) === assistantMessageId
          ? { ...msg, message: newLoadingText }
          : msg
      ));
    }, 1500); // Change message every 1.5 seconds

    try {
      // Use streaming endpoint
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/chat/query/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          message: inputMessage.trim(),
          document_ids: selectedDocuments,
          project_id: selectedProject || undefined,
          context_limit: 12,
          deep_research: deepResearchEnabled,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body reader available');
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let fullResponse = '';
      let sources: any[] = [];
      let documentReferences: number[] = [];
      let citations: any[] = [];
      let mindmapMarkdown: string | null = null;
      let mindmapMetadata: any = null;
      let metadata: any = null;
      let streamStarted = false;

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6)); // Remove 'data: ' prefix

              if (data.error) {
                throw new Error(data.error);
              }

              if (data.type === 'sources_with_id') {
                sources = data.sources || [];
                documentReferences = data.document_references || [];

                const finalId = data.sources?.[0]?.metadata?.id;
                if (finalId) {
                  console.log('📊 Received final ID from sources_with_id:', finalId);
                  const currentSources = sources;
                  const currentDocumentReferences = documentReferences;
                  setMessages(prev => prev.map(msg =>
                    (msg.stream_id || msg.id) === assistantMessageId
                      ? {
                        ...msg,
                        id: Number(finalId) || msg.id,
                        sources: currentSources,
                        document_references: currentDocumentReferences,
                      }
                      : msg
                  ));
                }
              } else if (data.type === 'sources') {
                sources = data.sources || [];
                documentReferences = data.document_references || [];
              } else if (data.type === 'content') {
                // First content chunk - stop loading animation and start streaming
                if (!streamStarted) {
                  clearInterval(loadingInterval);
                  streamStarted = true;
                }

                fullResponse += data.content;

                // Update the assistant message with streaming content
                const currentFullResponse = fullResponse;
                const currentSources = sources;
                const currentDocumentReferences = documentReferences;

                setMessages(prev => prev.map(msg =>
                  (msg.stream_id || msg.id) === assistantMessageId
                    ? {
                      ...msg,
                      message: currentFullResponse,
                      sources: currentSources,
                      document_references: currentDocumentReferences,
                    }
                    : msg
                ));
              } else if (data.type === 'citations') {
                // Handle citations
                citations = data.citations || [];
                console.log('📚 Received citations:', citations);
              } else if (data.type === 'mindmap') {
                // Handle mindmap
                mindmapMarkdown = data.mindmap_markdown || null;
                mindmapMetadata = data.mindmap_metadata || null;
                console.log('🗺️ Received mindmap:', mindmapMarkdown);
              } else if (data.type === 'metadata') {
                // Handle metadata - update message immediately
                metadata = data.metadata || null;
                console.log('📊 Received pipeline_metadata:', metadata);

                // Update message with pipeline_metadata immediately
                const currentMetadata = metadata;

                setMessages(prev => prev.map(msg =>
                  (msg.stream_id || msg.id) === assistantMessageId
                    ? {
                      ...msg,
                      id: Number(currentMetadata.id) || msg.id,
                      pipeline_metadata: currentMetadata,
                    }
                    : msg
                ));
              } else if (data.type === 'done') {
                // Streaming complete
                break;
              }
            } catch (parseError) {
              console.warn('Failed to parse streaming data:', parseError);
            }
          }
        }
      }

      // Final update with complete response including citations and mindmap
      setMessages(prev => prev.map(msg =>
        (msg.stream_id || msg.id) === assistantMessageId
          ? {
            ...msg,
            message: fullResponse || 'No response received',
            sources: sources,
            document_references: documentReferences,
            citations: citations,
            mindmap_markdown: mindmapMarkdown,
            mindmap_metadata: mindmapMetadata,
            pipeline_metadata: metadata,
          }
          : msg
      ));

    } catch (err) {
      clearInterval(loadingInterval);
      setLoadingMessageId(null);
      setError('Failed to send message. Please try again.');
      console.error('Send message error:', err);

      // Update the assistant message with error
      setMessages(prev => prev.map(msg =>
        (msg.stream_id || msg.id) === assistantMessageId
          ? {
            ...msg,
            message: 'Sorry, I encountered an error processing your message. Please try again.',
            is_error: true,
          }
          : msg
      ));
    } finally {
      clearInterval(loadingInterval);
      setLoadingMessageId(null);
      setIsLoading(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const applySuggestion = (suggestion: string) => {
    setInputMessage(suggestion);
  };

  const filteredDocuments = projectDocuments.filter(doc =>
    (doc.name || doc.display_name || doc.original_filename || doc.filename || '')
      .toLowerCase()
      .includes(documentSearchTerm.toLowerCase())
  );

  const clearChat = async () => {
    setIsDeletingChat(true); // Start loading
    try {
      if (selectedProject) {
        await chatAPI.deleteChatHistory(selectedProject);
        console.log('Chat history deleted successfully for project:', selectedProject);
      }

      const welcomeMessage = {
        id: Date.now(),
        type: 'assistant' as const,
        message: "Chat history cleared! How can I help you with your documents?",
        timestamp: new Date().toISOString()
      };
      setMessages([welcomeMessage]);
      // Clear cached messages
      localStorage.removeItem('chat_messages');
      setClearChatDialogOpen(false); // Close dialog on success
    } catch (error) {
      console.error('Failed to delete chat history:', error);
      setError('Failed to delete chat history. Please try again.');
    } finally {
      setIsDeletingChat(false); // End loading
    }
  };

  const downloadChatAsPDF = () => {
    const chatContainer = document.getElementById('chat-container');
    if (chatContainer) {
      // Create a temporary clone to avoid modifying the visible DOM
      const tempContainer = chatContainer.cloneNode(true) as HTMLElement;

      // Ensure all backgrounds are white, text is black, and borders are removed
      const allElements = tempContainer.querySelectorAll('*');
      allElements.forEach(el => {
        const element = el as HTMLElement;
        element.style.backgroundColor = '#ffffff';
        element.style.color = '#000000';
        element.style.border = 'none';
        element.style.borderRadius = '0';
        element.style.boxShadow = 'none';
        element.style.backgroundImage = 'none';
      });
      tempContainer.style.backgroundColor = '#ffffff';
      tempContainer.style.color = '#000000';
      tempContainer.style.border = 'none';
      tempContainer.style.borderRadius = '0';
      tempContainer.style.boxShadow = 'none';
      tempContainer.style.fontSize = '0.75rem'; // Reduce base font size

      // Temporarily append to body but hide it
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.width = '210mm'; // A4 width
      tempContainer.style.padding = '20mm'; // Add padding around the content
      tempContainer.style.boxSizing = 'border-box'; // Include padding in width
      document.body.appendChild(tempContainer);

      html2canvas(tempContainer, {
        backgroundColor: '#ffffff',
        scale: 2, // Increase scale for better resolution
        useCORS: true,
        logging: false,
        windowWidth: tempContainer.scrollWidth, // Capture full scrollable width
        windowHeight: tempContainer.scrollHeight // Capture full scrollable height
      }).then(canvas => {
        const imgData = canvas.toDataURL('image/jpeg', 0.8);
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight(); // A4 height in mm
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;

        const ratio = pdfWidth / imgWidth;
        const imgHeightScaled = imgHeight * ratio;

        let heightLeft = imgHeightScaled;
        let position = 0;

        // Add first page
        pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, imgHeightScaled);
        heightLeft -= pdfHeight;

        // Add subsequent pages
        while (heightLeft > 0) {
          position = heightLeft - imgHeightScaled; // Calculate position for the next slice
          pdf.addPage();
          pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, imgHeightScaled);
          heightLeft -= pdfHeight;
        }
        pdf.save('chat.pdf');

        // Clean up
        document.body.removeChild(tempContainer);
      });
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden'
    }}>
      {/* Top Navbar with Project and Document Selection */}
      <Paper sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        mb: 3,
      }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          AI Document Assistant
        </Typography>

        <Box sx={{ flex: 1 }} />

        {/* Project Selection */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel>Project</InputLabel>
            <Select
              value={selectedProject}
              label="Project"
              onChange={(e) => handleProjectChange(e.target.value)}
            >
              <MenuItem value="">
                <em>Choose project...</em>
              </MenuItem>
              {loadingProjects ? (
                Array.from({ length: 3 }).map((_, index) => (
                  <MenuItem key={`skeleton-${index}`} disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Box sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: 'grey.300',
                        animation: 'pulse 1.5s ease-in-out infinite'
                      }} />
                      <Box sx={{
                        height: 16,
                        bgcolor: 'grey.300',
                        borderRadius: 1,
                        flex: 1,
                        animation: 'pulse 1.5s ease-in-out infinite'
                      }} />
                    </Box>
                  </MenuItem>
                ))
              ) : (
                projects.map(project => (
                  <MenuItem key={project.id} value={project.id.toString()}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FolderIcon fontSize="small" color="primary" />
                      {project.name}
                    </Box>
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <Tooltip title="Refresh Projects">
            <IconButton
              size="small"
              onClick={loadProjects}
              disabled={loadingProjects}
              sx={{
                border: 1,
                borderColor: 'divider',
                '&:hover': { borderColor: 'primary.main' }
              }}
            >
              {loadingProjects ? <CircularProgress size={20} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Document Selection Button */}
        <Button
          variant="outlined"
          startIcon={<FileIcon />}
          onClick={(e) => setDocumentMenuAnchor(e.currentTarget)}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
          }}
        >
          {selectedDocuments.length} docs selected
        </Button>

        {/* Document Selection Dropdown Menu */}
        <Menu
          anchorEl={documentMenuAnchor}
          open={Boolean(documentMenuAnchor)}
          onClose={() => setDocumentMenuAnchor(null)}
          PaperProps={{
            sx: {
              width: 400,
              maxHeight: 500,
            }
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Select Documents
              </Typography>
              <Tooltip title="Refresh Documents">
                <IconButton
                  size="small"
                  onClick={loadProjectDocuments}
                  disabled={loadingDocuments || !selectedProject}
                >
                  {loadingDocuments ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>

            {/* Document Search */}
            {projectDocuments.length > 0 && (
              <TextField
                size="small"
                fullWidth
                placeholder="Search documents..."
                value={documentSearchTerm}
                onChange={(e) => setDocumentSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: documentSearchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setDocumentSearchTerm('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
            )}

            {/* Select All / None */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {selectedDocuments.length}/{filteredDocuments.length} selected
              </Typography>
              <Button
                size="small"
                onClick={handleToggleAllDocuments}
                disabled={filteredDocuments.length === 0}
              >
                {selectedDocuments.length === filteredDocuments.length ? 'None' : 'All'}
              </Button>
            </Box>

            <Divider sx={{ mb: 1 }} />

            {/* Document List */}
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              {loadingDocuments ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : filteredDocuments.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <FileIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {selectedProject ? 'No AI-ready documents' : 'Select a project first'}
                  </Typography>
                </Box>
              ) : (
                <List dense>
                  {filteredDocuments.map((doc) => (
                    <ListItem key={doc.id} disablePadding>
                      <ListItemButton
                        onClick={() => handleDocumentToggle(doc.id)}
                        sx={{ borderRadius: 1 }}
                      >
                        <ListItemIcon>
                          <Checkbox
                            checked={selectedDocuments.includes(doc.id)}
                            size="small"
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" noWrap>
                              {doc.name || doc.display_name || doc.original_filename || doc.filename}
                            </Typography>
                          }
                          secondary={doc.file_type?.toUpperCase()}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Box>
        </Menu>

        {/* Actions */}
        <Tooltip title="Delete Chat History">
          <IconButton onClick={() => setClearChatDialogOpen(true)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Download Chat as PDF">
          <IconButton onClick={downloadChatAsPDF}>
            <DownloadIcon />
          </IconButton>
        </Tooltip>

        {/* Toggle Information Panel Button (for mobile) - Keep in header for mobile drawer */}
        {isMobile && (
          <Tooltip title="Toggle Information Panel">
            <IconButton onClick={toggleInfoPanel}>
              {isInfoPanelOpen ? <InfoIcon /> : <InfoOutlinedIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Paper>

      {/* Main Content Area */}
      <Box sx={{
        display: 'flex',
        gap: 2,
        flex: 1,
        overflow: 'hidden'
      }}>
        {/* Main Chat Area */}
        <Box sx={{
          flex: 1, // Allow chat area to grow and shrink
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0, // Allows shrinking
          border: 1,
          borderColor: 'divider',
          borderRadius: '16px',
          position: 'relative', // Needed for absolute positioning of the toggle button
        }}>

          <ChatContainer>

            <Box id="chat-container" sx={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Box
                ref={messagesContainerRef}
                sx={{
                  flex: 1,
                  overflow: 'auto',
                  p: 2,
                  bgcolor: 'background.default',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  // Add padding-right to account for the button, so text doesn't go under it
                  pr: !isMobile && isInfoPanelOpen ? theme.spacing(8) : theme.spacing(2), // Adjust padding based on button presence and screen size
                }}
              >
                {messages.map((message) => (
                  <ChatMessage
                    key={message.id}
                    message={message}
                    onSourceClick={(source) => {
                      console.log('📌 onSourceClick called with source:', source);
                      // This is called from the old citation system, not used anymore
                      // The new system uses window.showSourceModal directly
                    }}
                    formatTimestamp={formatTimestamp}
                    parseContent={(content) => content} // Just return content as-is, let MarkdownRenderer handle it
                    isLoading={loadingMessageId === (message.stream_id || message.id)}
                    projectDocuments={projectDocuments}
                  >
                    {message.type === 'assistant' &&
                      !message.is_error &&
                      (loadingMessageId !== (message.stream_id || message.id) || message.pipeline_metadata?.id != null) && (
                        <MessageFeedback
                          messageId={message.id}
                          initialFeedback={message.feedback || null}
                          message={message.message}
                        />
                      )}
                  </ChatMessage>
                ))}

                {/* Loading skeleton is now handled by the loading message in the messages array */}

                <div ref={messagesEndRef} />
              </Box>

              {/* Scroll to Bottom Button */}
              {showScrollButton && (
                <IconButton
                  onClick={scrollToBottom}
                  sx={{
                    position: 'absolute',
                    bottom: 20,
                    right: 20,
                    width: 48,
                    height: 48,
                    bgcolor: 'primary.main',
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.2s ease-in-out',
                    zIndex: 10,
                  }}
                >
                  <ExpandMoreIcon />
                </IconButton>
              )}
            </Box>

            {/* Suggested Prompts */}
            {messages.length === 1 && selectedDocuments.length > 0 && (
              <Box sx={{ borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper', p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Try asking:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {suggestedPrompts.map((prompt, index) => (
                    <Chip
                      key={index}
                      label={prompt}
                      variant="outlined"
                      size="small"
                      clickable
                      onClick={() => applySuggestion(prompt)}
                      icon={<SparkleIcon />}
                      sx={{
                        '&:hover': {
                          bgcolor: 'primary.50',
                          borderColor: 'primary.main',
                        }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Error message */}
            {error && (
              <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Quick Actions and Deep Research */}
            {selectedDocuments.length > 0 && (
              <Box sx={{
                borderTop: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                p: 1, // Reduced padding from 2 to 1
                borderBottom: 1
              }}>
                <Box
                  onClick={() => setIsQuickAnalysisCollapsed(!isQuickAnalysisCollapsed)}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 0, // Set margin-bottom to 0 when collapsed
                    cursor: 'pointer',
                    p: 1,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    Quick Analysis:
                  </Typography>
                  {isQuickAnalysisCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                </Box>
                <Collapse in={!isQuickAnalysisCollapsed}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}> {/* Removed pb: 1 */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FileIcon />}
                        onClick={() => applySuggestion('Summarize the key points from these documents')}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontSize: '0.875rem',
                          '&:hover': {
                            bgcolor: 'primary.main',
                            color: 'white',
                            borderColor: 'primary.main',
                          }
                        }}
                      >
                        Summarize
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<HistoryIcon />}
                        onClick={() => applySuggestion('Create a timeline of events mentioned in the documents')}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontSize: '0.875rem',
                          '&:hover': {
                            bgcolor: 'primary.main',
                            color: 'white',
                            borderColor: 'primary.main',
                          }
                        }}
                      >
                        Timeline
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ShareIcon />}
                        onClick={() => applySuggestion('Compare the documents and highlight differences')}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontSize: '0.875rem',
                          '&:hover': {
                            bgcolor: 'primary.main',
                            color: 'white',
                            borderColor: 'primary.main',
                          }
                        }}
                      >
                        Compare
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<WarningIcon />}
                        onClick={() => applySuggestion('Find any inconsistencies or issues in the documents')}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontSize: '0.875rem',
                          '&:hover': {
                            bgcolor: 'primary.main',
                            color: 'white',
                            borderColor: 'primary.main',
                          }
                        }}
                      >
                        Find Issues
                      </Button>
                    </Box>
                    {/* Deep Research Toggle - moved here and aligned right */}
                  </Box>
                </Collapse>
              </Box>
            )}

            <ChatContainer.Input>
              <Box component="form" onSubmit={handleSendMessage} sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 0 }}>
                {/* Enhanced Deep Research Toggle */}
                <Tooltip title={deepResearchEnabled ? "Deep Research Enabled - Click to disable" : "Enable Deep Research for comprehensive web-based answers"}>
                  <Box
                    onClick={() => {
                      if (!deepResearchEnabled) {
                        setDeepResearchDialogOpen(true);
                      } else {
                        setDeepResearchEnabled(false);
                      }
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: { xs: 0.5, sm: 1 },
                      px: { xs: 1, sm: 2 },
                      height: '56px',
                      borderRadius: 3,
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: deepResearchEnabled ? 'primary.main' : 'divider',
                      bgcolor: deepResearchEnabled ? 'primary.50' : 'background.paper',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: deepResearchEnabled ? 'primary.100' : 'primary.50',
                        transform: 'scale(1.02)',
                      },
                      flexShrink: { xs: 1, sm: 0 },
                      minWidth: { xs: 'auto', sm: '140px' },
                    }}
                  >
                    <SearchIcon 
                      sx={{ 
                        fontSize: 20,
                        color: deepResearchEnabled ? 'primary.main' : 'text.secondary',
                        transition: 'color 0.3s ease',
                      }} 
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontWeight: 600,
                          color: deepResearchEnabled ? 'primary.main' : 'text.secondary',
                          display: 'block',
                          lineHeight: 1.2,
                        }}
                      >
                        Deep Research
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontSize: '0.65rem',
                          color: deepResearchEnabled ? 'primary.dark' : 'text.disabled',
                          display: 'block',
                        }}
                      >
                        {deepResearchEnabled ? 'ON' : 'OFF'}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: 36,
                        height: 20,
                        borderRadius: 10,
                        bgcolor: deepResearchEnabled ? 'primary.main' : 'grey.300',
                        position: 'relative',
                        transition: 'background-color 0.3s ease',
                      }}
                    >
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          bgcolor: 'white',
                          position: 'absolute',
                          top: 2,
                          left: deepResearchEnabled ? 18 : 2,
                          transition: 'left 0.3s ease',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        }}
                      />
                    </Box>
                  </Box>
                </Tooltip>                <TextField
                  fullWidth
                  multiline
                  maxRows={4}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => {
                    // Enter sends message, Shift+Enter creates new line
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage(e as any);
                    }
                  }}
                  placeholder="Ask about your documents..."
                  disabled={isLoading}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ 
                        height: '100%', 
                        maxHeight: 'none',
                        alignItems: 'center',
                        mt: 0
                      }}>
                        <MessageSquare color="action" />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      bgcolor: 'background.paper',
                      minHeight: '56px',
                      wordBreak: 'break-word',
                      overflow: 'hidden',
                      alignItems: 'center',
                      padding: '8px 14px'
                    },
                    '& .MuiInputBase-inputMultiline': {
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      textOverflow: 'ellipsis',
                      marginTop: 0,
                      marginBottom: 0
                    },
                    '& .MuiInputAdornment-root': {
                      height: '100%',
                      maxHeight: 'none'
                    },
                    flex: { xs: '1 1 auto', sm: '1 1 auto' }, // Allow flexible sizing
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isLoading || !inputMessage.trim() || selectedDocuments.length === 0}
                  startIcon={isLoading ? <CircularProgress size={16} /> : <Send />}
                  sx={{
                    borderRadius: 3,
                    px: { xs: 2, sm: 3 },
                    height: '56px', // Match the default height of MUI TextField
                    minWidth: { xs: 90, sm: 120 },
                    whiteSpace: 'nowrap',
                  }}
                >
                  {isLoading ? 'Sending...' : 'Send'}
                </Button>
              </Box>
            </ChatContainer.Input>
          </ChatContainer>
        </Box>

        {/* Permanent Information Panel (Kotaemon-style) */}
        {isMobile ? (
          <Drawer
            anchor="right"
            open={isInfoPanelOpen}
            onClose={() => setIsInfoPanelOpen(false)}
            PaperProps={{
              sx: { width: '80%', maxWidth: 400 },
            }}
          >
            <InformationPanelPermanent
              mindmapMarkdown={selectedMessage?.mindmap_markdown}
              mindmapMetadata={selectedMessage?.mindmap_metadata}
              citations={selectedMessage?.pipeline_metadata?.citations?.citations || []}
              sources={selectedMessage?.sources || []}
              selectedCitationIndex={selectedCitationIndex ?? undefined}
              onCitationClick={(citation) => {
                console.log('Citation clicked in info panel:', citation);
              }}
              isOpen={isInfoPanelOpen}
              onToggle={toggleInfoPanel}
            />
          </Drawer>
        ) : ( // For non-mobile screens
          <Box
            sx={{
              width: isInfoPanelOpen ? '400px' : '5%',
              minWidth: isInfoPanelOpen ? '400px' : '5%',
              maxWidth: isInfoPanelOpen ? '400px' : '5%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.paper',
              ml: isInfoPanelOpen ? 2 : 0, // Animate margin as well
              borderLeft: isInfoPanelOpen ? 1 : 0, // Add border only when open
              borderColor: 'divider',
              borderRadius: '16px',
              overflow: 'hidden',
              transition: 'width 0.3s ease-in-out, min-width 0.3s ease-in-out, max-width 0.3s ease-in-out, margin-left 0.3s ease-in-out',
            }}
          >
            <InformationPanelPermanent
              mindmapMarkdown={selectedMessage?.mindmap_markdown}
              mindmapMetadata={selectedMessage?.mindmap_metadata}
              citations={selectedMessage?.pipeline_metadata?.citations?.citations || []}
              sources={selectedMessage?.sources || []}
              selectedCitationIndex={selectedCitationIndex ?? undefined}
              onCitationClick={(citation) => {
                console.log('Citation clicked in info panel:', citation);
              }}
              isOpen={isInfoPanelOpen}
              onToggle={toggleInfoPanel}
            />
          </Box>
        )}

      </Box>


      {/* Clear Chat Confirmation Dialog */}
      <Dialog
        open={clearChatDialogOpen}
        onClose={() => setClearChatDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon color="error" />
            Delete Chat History
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to clear the chat history? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setClearChatDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={clearChat}
            disabled={isDeletingChat} // Disable while loading
            startIcon={isDeletingChat ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {isDeletingChat ? 'Deleting...' : 'Delete Chat'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deep Research Confirmation Dialog */}
      <Dialog
        open={deepResearchDialogOpen}
        onClose={() => setDeepResearchDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
          }
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SearchIcon color="primary" sx={{ fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Enable Deep Research?
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6, color: 'text.secondary' }}>
            In Deep Research, the AnCore will search the internet and other external resources to provide more thorough and up-to-date responses. This may take longer but will give you more comprehensive answers with current market data and trends.
          </Typography>

          {/* Feature boxes */}
          <Stack spacing={2}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              bgcolor: 'primary.50',
              borderRadius: 2,
              border: 1,
              borderColor: 'primary.100',
            }}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: 'primary.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <SearchIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                Real-time web search
              </Typography>
            </Box>

            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              bgcolor: 'primary.50',
              borderRadius: 2,
              border: 1,
              borderColor: 'primary.100',
            }}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: 'primary.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Box sx={{
                  width: 20,
                  height: 20,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}>
                  <Box sx={{ width: '100%', height: 3, bgcolor: 'primary.main', borderRadius: 0.5 }} />
                  <Box sx={{ width: '80%', height: 3, bgcolor: 'success.main', borderRadius: 0.5 }} />
                  <Box sx={{ width: '60%', height: 3, bgcolor: 'warning.main', borderRadius: 0.5 }} />
                </Box>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                Current market data
              </Typography>
            </Box>

            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              bgcolor: 'primary.50',
              borderRadius: 2,
              border: 1,
              borderColor: 'primary.100',
            }}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: 'primary.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <SearchIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                Enhanced research capabilities
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => setDeepResearchDialogOpen(false)}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setDeepResearchEnabled(true);
              setDeepResearchDialogOpen(false);
            }}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              minWidth: 160,
            }}
          >
            Enable Deep Search
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Chat;
